import { CalendarDays } from '@tamagui/lucide-icons';
import { Group } from '@unistocks/components/group/group';
import { Column, Row } from '@unistocks/components/layout/flex';
import { Spinner } from '@unistocks/components/loading/spinner/spinner';
import { HeaderIcon } from '@unistocks/components/modal/header-icon';
import { Sheet } from '@unistocks/components/sheet/sheet';
import { H4 } from '@unistocks/components/text/text';
import { AssetEventListItem } from '@unistocks/features/dashboard/components/dashboard-data/date/components/asset-event';
import { ValueListItem } from '@unistocks/features/dashboard/components/value-list-item/value-list-item';
import { DashboardEventDto, PortfolioDto } from '@unistocks/proxy/axios-client';
import { useExchangeRatesGetExchangeRatesQuery } from '@unistocks/proxy/axios-client/ExchangeRatesQuery';
import { formatCurrency, formatDate, t } from '@unistocks/shared/localization/index.ts';
import { getTotalAssetQuantity } from '@unistocks/shared/utils/asset-utils';
import { distinct } from '@unistocks/shared/utils/util';
import { ReactNode, useState } from 'react';
import { Adapt, ListItem, Popover, Separator, useAdaptIsActive } from 'tamagui';

export type AssetEventDetailsProps = {
  event: DashboardEventDto;
  portfolios: PortfolioDto[];
  children: ReactNode;
};

export const AssetEventDetails: React.FC<AssetEventDetailsProps> = ({
  event,
  portfolios,
  children
}) => {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen} placement="right" stayInFrame allowFlip size={'$5'}>
      <Popover.Trigger asChild>{children}</Popover.Trigger>

      <Popover.Content
        elevation={'$1'}
        padding={'$3'}
        borderWidth={1}
        borderColor={'$borderColor'}
        enterStyle={{ x: -10, opacity: 0 }}
        exitStyle={{ x: -10, opacity: 0 }}
        transition={'quickLessBouncy'}
      >
        <Popover.Arrow borderWidth={1} borderColor={'$borderColor'} />
        <AssetEventDetailsContent event={event} portfolios={portfolios} />
      </Popover.Content>

      <Adapt when={'max-sm'}>
        <Sheet
          enableDynamicSizing
          headerIcon={<HeaderIcon Icon={CalendarDays} color={'$headingFont'} />}
          headerTitle={formatDate(event.date, { month: 'long', day: 'numeric' })}
        >
          <Adapt.Contents />
        </Sheet>
      </Adapt>
    </Popover>
  );
};

AssetEventDetails.displayName = 'AssetEventDetails';

type ContentProps = {
  event: DashboardEventDto;
  portfolios: PortfolioDto[];
};

const AssetEventDetailsContent: React.FC<ContentProps> = ({ event, portfolios }) => {
  const isAdapted = useAdaptIsActive();

  const portfoliosWithSelectedAsset = portfolios.filter(p =>
    p.sections.some(section =>
      section.assets.some(asset => asset.id === event.relatedEntities[0].id)
    )
  );

  const portfolioCurrencies = distinct(
    portfoliosWithSelectedAsset
      .map(portfolio => portfolio.currency)
      .filter(x => x !== event.value?.currency)
  );

  const {
    data: exchangeRateRes,
    isSuccess: isExchangeRateSuccess,
    isLoading: isExchangeRateLoading
  } = useExchangeRatesGetExchangeRatesQuery(
    {
      baseCurrency: event.value?.currency!,
      toCurrencies: portfolioCurrencies
    },
    { enabled: portfolioCurrencies.length > 0 }
  );

  const dateLabel = formatDate(event.date, { month: 'long', day: 'numeric' });

  return (
    <>
      {!isAdapted && (
        <Row gap={'$2'} mb={'$2'} w={'100%'} borderBottomWidth={1} borderColor={'$borderColor'}>
          <HeaderIcon Icon={CalendarDays} size={'$1'} color={'$headingFont'} />
          <H4 flex={1}>{dateLabel}</H4>
        </Row>
      )}
      <Group separator={<Separator />}>
        {portfolios.map(portfolio => {
          const value = event.value;
          const asset = portfolio.sections
            .flatMap(section => section.assets)
            .find(asset => asset.id === event.relatedEntities[0].id);
          const quantity = asset && getTotalAssetQuantity(asset);
          const exchangeRate = exchangeRateRes?.items.find(
            x => x.toCurrency === portfolio.currency
          );

          if (!quantity || !value) return null;

          return (
            <Group.Item key={portfolio.id}>
              <H4>{portfolio.name}</H4>
              <AssetEventListItem
                px={0}
                event={event}
                asset={asset}
                hoverStyle={{
                  bg: '$background'
                }}
                pressStyle={{
                  bg: '$background'
                }}
                subTitle={t('::AssetEvent:ExpectedDividendPayout')}
                iconAfter={() => (
                  <Column>
                    <ValueListItem.Value>
                      {formatCurrency(quantity * value.value, value.currency)}
                    </ValueListItem.Value>
                    {portfolio.currency !== value.currency ? (
                      isExchangeRateLoading ? (
                        <Spinner />
                      ) : isExchangeRateSuccess && exchangeRate ? (
                        <ListItem.Subtitle>
                          {formatCurrency(
                            quantity * value.value * exchangeRate.rate,
                            portfolio.currency
                          )}
                        </ListItem.Subtitle>
                      ) : null
                    ) : null}
                  </Column>
                )}
              />
            </Group.Item>
          );
        })}
      </Group>
    </>
  );
};

AssetEventDetailsContent.displayName = 'AssetEventDetailsContent';
